import axiosInstance from '../services/axiosInstance';

interface SignedUrlResponse {
  error: number;
  message: string;
  data: {
    uploadUrl: string;
    imageUrl: string;
    expiresAt: string;
  };
}

const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

/**
 * Validates a file for profile image upload
 * @param file - The file to validate
 * @throws Error if validation fails
 */
const validateFile = (file: File): void => {
  // Validate file type
  if (!ALLOWED_FILE_TYPES.includes(file.type.toLowerCase())) {
    throw new Error('Invalid file type. Allowed types: JPEG, PNG, GIF, WebP');
  }

  // Validate file size
  if (file.size > MAX_FILE_SIZE) {
    throw new Error('File size exceeds 5MB limit');
  }
};

/**
 * Uploads a profile image to Firebase Storage using signed URL approach
 * @param file - The image file to upload
 * @param email - The user email for file path organization
 * @returns Promise resolving to the public URL of the uploaded image
 * @throws Error if upload fails at any step
 */
export const uploadProfileImageToStorage = async (
  file: File,
  email: string
): Promise<string> => {
  try {
    // Step 1: Validate the file
    validateFile(file);

    // Step 2: Request signed URL from backend
    const response = await axiosInstance.post<SignedUrlResponse>(
      '/users/getProfileImageUploadUrl',
      {
        fileName: file.name,
        fileType: file.type,
        email: email,
      }
    );

    // Check if backend returned an error
    if (response.data.error !== 0) {
      throw new Error(response.data.message || 'Failed to get upload URL');
    }

    const { uploadUrl, imageUrl } = response.data.data;

    if (!uploadUrl || !imageUrl) {
      throw new Error('Invalid response from server: missing upload or image URL');
    }

    // Step 3: Upload file directly to Firebase Storage using signed URL
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    });

    if (!uploadResponse.ok) {
      throw new Error(
        `Failed to upload image to storage: ${uploadResponse.status} ${uploadResponse.statusText}`
      );
    }

    // Step 4: Return the image URL
    return imageUrl;
  } catch (error) {
    // Re-throw with more context if it's not already an Error object
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred during image upload');
  }
};

